import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';
import SalesQuickCreateModal from './components/SalesQuickCreateModal';

const SalesTasksPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingTaskId, setSavingTaskId] = useState('');
  const [error, setError] = useState('');
  const [tasks, setTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [companies, setCompanies] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [quickCreateMode, setQuickCreateMode] = useState('');

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('sales/activities/tasks/', {
        params: {
          mine: true,
          status: statusFilter || undefined
        }
      });
      setTasks(response.data?.tasks || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_TASKS'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get('sales/companies/')
      .then(response => {
        if (!mounted) return;
        setCompanies(response.data?.companies || []);
      })
      .catch(() => {
        if (!mounted) return;
        setCompanies([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get('sales/opportunities/', { params: { status: 'open' } })
      .then(response => {
        if (!mounted) return;
        setOpportunities(response.data?.opportunities || []);
      })
      .catch(() => {
        if (!mounted) return;
        setOpportunities([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const completeTask = useCallback(async taskId => {
    setSavingTaskId(String(taskId));
    setError('');
    try {
      await axiosInstance.post(`sales/activities/tasks/${taskId}/complete/`);
      await loadTasks();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_COMPLETE_TASK'));
    } finally {
      setSavingTaskId('');
    }
  }, [loadTasks, t]);

  const counters = useMemo(() => {
    const result = { pending: 0, done: 0, overdue: 0 };
    tasks.forEach(item => {
      result[item.status] = (result[item.status] || 0) + 1;
    });
    return result;
  }, [tasks]);

  return (
    <>
      <PageHeader title={t('SALES.TASKS')} className="mb-3" />

      <Card className="mb-3 border-primary-subtle">
        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="text-primary fw-semi-bold">{t('SALES.TASK_DISCIPLINE')}</div>
            <div className="text-600 fs--1">{t('SALES.TASK_DISCIPLINE_HELP')}</div>
          </div>
          <Button size="sm" onClick={() => setQuickCreateMode('task')}>
            {t('SALES.NEW_TASK')}
          </Button>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body className="d-flex flex-wrap align-items-center gap-3">
          <Form.Select
            style={{ maxWidth: 220 }}
            value={statusFilter}
            onChange={event => setStatusFilter(event.target.value)}
          >
            <option value="">{t('SALES.ALL')}</option>
            <option value="pending">{t('SALES.PENDING')}</option>
            <option value="done">{t('SALES.DONE')}</option>
            <option value="overdue">{t('SALES.OVERDUE')}</option>
          </Form.Select>
          <div className="d-flex gap-2 align-items-center">
            <Badge bg="soft-warning" text="warning">{t('SALES.PENDING')}: {counters.pending || 0}</Badge>
            <Badge bg="soft-success" text="success">{t('SALES.DONE')}: {counters.done || 0}</Badge>
            <Badge bg="soft-danger" text="danger">{t('SALES.OVERDUE')}: {counters.overdue || 0}</Badge>
          </div>
        </Card.Body>
      </Card>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('SALES.TASK')}</th>
                  <th>{t('SALES.PRIORITY')}</th>
                  <th>{t('SALES.DUE')}</th>
                  <th>{t('SALES.STATUS')}</th>
                  <th className="text-end">{t('SALES.TABLE_ACTIONS')}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.length ? tasks.map(item => (
                  <tr key={item.id}>
                    <td>
                      <div className="fw-semi-bold">{item.title}</div>
                      <div className="text-600 fs--1">{item.description || '-'}</div>
                    </td>
                    <td>{item.priority}</td>
                    <td>{item.due_at ? new Date(item.due_at).toLocaleString() : '-'}</td>
                    <td>
                      <Badge bg={item.status === 'done' ? 'success' : item.status === 'overdue' ? 'danger' : 'warning'}>
                        {item.status}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        {item.opportunity_id ? (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => navigate(`/sales/opportunity/${item.opportunity_id}`)}
                          >
                            {t('SALES.OPEN_DEAL')}
                          </Button>
                        ) : null}
                        {item.status === 'pending' ? (
                          <Button
                            size="sm"
                            variant="outline-success"
                            disabled={savingTaskId === String(item.id)}
                            onClick={() => completeTask(item.id)}
                          >
                            {savingTaskId === String(item.id) ? t('SALES.SAVING') : t('SALES.MARK_DONE')}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} className="text-center text-600 py-4">
                      <div className="mb-3">{t('SALES.EMPTY_TASKS_TABLE')}</div>
                      <Button size="sm" variant="outline-primary" onClick={() => setQuickCreateMode('task')}>
                        {t('SALES.NEW_TASK')}
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <SalesQuickCreateModal
        show={Boolean(quickCreateMode)}
        mode={quickCreateMode || 'task'}
        onHide={() => setQuickCreateMode('')}
        companies={companies}
        opportunities={opportunities}
        onCreated={payload => {
          if (payload?.type === 'task' && payload.task) {
            setTasks(previous => [payload.task, ...previous]);
          }
        }}
      />
    </>
  );
};

export default SalesTasksPage;
